import Translation from "src/translations/locales/translation";

import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { RoutesEnum } from "src/enums/Routes";
import { withTranslation } from "react-i18next";
import { Form } from "src/shared-components/form";
import { EmailValidator } from "src/components/email";
import { WithTFunction } from "src/types/WithTFunction";
import { DynamicForm } from "src/composable/base-dynamic-form";
import { FirebaseAuthErrorHandler } from "src/utils/FirebaseAuthErrorHandler";
import { ArrowSquareLeftIcon } from "src/assets/icons/components/ArrowSquareLeftIcon";
import { SimpleTextInput } from "src/shared-components/form/fields/text-fields/simple-text-input";
import { SimpleElevatedButton } from "src/shared-components/form/fields/buttons/simple-elevated-button";

import {
  ResetPasswordScreenFormProps,
  ResetPasswordScreenFormState,
} from "./index.interfaces";

class ResetPasswordScreenForm extends DynamicForm<
  WithTFunction<ResetPasswordScreenFormProps>,
  ResetPasswordScreenFormState
> {
  _emailValidator: EmailValidator;

  state: Readonly<ResetPasswordScreenFormState> = {
    isLoading: false,
  };

  constructor(props: WithTFunction<ResetPasswordScreenFormProps>) {
    super(props);
    this._emailValidator = new EmailValidator(props.t);
  }

  onDisableSubmitButton = (): boolean => {
    return false;
  };

  onBackToSignInScreenButtonClicked = () => {
    window.location.href = RoutesEnum.signin;
  };

  onSubmit = async () => {
    const { email } = this.props;

    // validate the email
    if (!email.trim()) return;
    if (this._emailValidator.validateEmail(email)) return;

    try {
      await this.props.onResetPasswordButtonClicked();
    } catch (e) {
      FirebaseAuthErrorHandler.handle(e, this.props.t);
    }
  };

  render(): ReactNode {
    const { t, email, onEmailChanged } = this.props;

    return (
      <section className="flex flex-col gap-8 desktop:gap-20 w-full max-w-[300px] py-16 tablet:py-28 desktop:py-0 desktop:min-w-[50vw] desktop:px-[12.5%]">
        {/* back to sign-in screen button */}
        <Link
          to={RoutesEnum.signin}
          className="cursor-pointer w-fit h-fit flex items-center gap-3"
        >
          <ArrowSquareLeftIcon className="stroke-on-background" />
          <h1 className="hidden tablet:block">
            {t(Translation.screens.resetPassword.backToSigninScreenButton)}
          </h1>
        </Link>
        <Form
          className="flex flex-col gap-6 w-full py-16 tablet:py-28 desktop:py-0"
          onSubmit={this.onSubmit}
        >
          {/* title and description */}
          <div className="flex flex-col gap-3">
            {/* title */}
            <h1 className="text-lg font-new-atten-bold">
              {t(Translation.screens.resetPassword.form.title).toUpperCase()}
            </h1>
            {/* description */}
            <p className="opacity-50 text-normal">
              {t(Translation.screens.resetPassword.form.subTitle)}
            </p>
          </div>
          {/* email input */}
          <SimpleTextInput
            id="resetPassword-email-input"
            name="resetPassword-email-input"
            placeholder={t(
              Translation.screens.resetPassword.form.emailPlaceholder
            )}
            value={email}
            onChange={onEmailChanged}
            maxLength={100}
            validator={this._emailValidator.validateEmail}
          />
          {/* submit button */}
          <SimpleElevatedButton
            id="resetPassword-submit-button"
            text={t(Translation.screens.resetPassword.form.submitButton)}
            onClick={this.onSubmit}
            disabled={false}
          />
        </Form>
      </section>
    );
  }
}

export default withTranslation()(ResetPasswordScreenForm);
